import { IconButton, Typography, withStyles } from '@material-ui/core';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import GetAppIcon from '@material-ui/icons/GetApp';
import { AttributesMixin, define, Events, Linked, memberOf, Store, type } from '@type-r/models';
import { useIO, useLinked, useLocalStorage, useModel } from '@type-r/react';
import { CompanyModel, globalStore } from 'api';
import { Activity } from 'api/activity';
import { AddFilter, asCurrency, asFilenameDateTime, asTimelessDate, Button, CompactTabs, CsvDownloader, CsvMapping, FiltersList, Flex, getQueryParam, Loading, Page, PopupIODialog, PrimaryButton, SelectSortBy, SmallTile, Tab, TileField, TilesList, toLinkedBoolean } from 'lqx-react';
import * as React from 'react';
import { useMemo } from 'react';
import { UserEntitlementCatalogs } from 'src/app/models/UserEntitlements';
import { execBookPayment, execReverseReconciliation, Payment, Reconciliation, ReconciliationFilters } from 'src/app/pages/inmatch/api';
import { Can } from 'src/app/services/can';
import { RowH2 } from '../../../../../../common/inmatch';
import { UploadPaymentFileDialog } from '../../bank-transactions/payments';
import { GenerateFeedConfirmationDialog, PaymentInformation } from '../../bank-transactions/payments/paymentInformation';
import { UnreconcileDialog } from './unmatch';

@define class State extends Store {
    static attributes = {
        reconciliations : [ Reconciliation ],
        companies : [CompanyModel],
        unmatching : memberOf( 'reconciliations', Reconciliation ),
        booking : memberOf( 'reconciliations', Reconciliation ),
        selected: memberOf('reconciliations', Reconciliation),
        filter : type(ReconciliationFilters),
        category : 'all',
        sortBy : {
            attr: 'createdOn',
            asc: false
        },
        reversing : memberOf( 'reconciliations', Reconciliation ),
    }
}
interface State extends AttributesMixin<typeof State>{}

const sortOptions = {
    status : 'Status',
    supplierName : 'Supplier Name',
    customerName : 'Customer Name',
    cashReceived : 'Cash Received',
    cashApplied : 'Cash Applied',
    valueDate : 'Value Date',
    statementDate : 'Statement Date',
    createdOn : 'Reconciliation Date',
}

const sortOptionsBooked = { ...sortOptions, bookedOn : 'Booked On' }

const localStorageFilterKey = 'settings.inmatch.payment.reconciled.filter';

const csvFields: CsvMapping<Reconciliation> = {
    'Payment Id' : 'paymentId',
    'Supplier' : 'supplierName',
    'Customer' : 'customerName',
    'Payor Agent' : 'payorAgentName',
    'Value Date' : x => asTimelessDate(x.valueDate),
    'Reconciliation date' : x => asTimelessDate(x.createdOn),
    'Booked Date': x => asTimelessDate(x.bookedOn),
    'Currency' : 'currency',
    'Cash received' : x => asCurrency(x.cashReceived, x.currency),
    'Cash applied' : x => asCurrency(x.cashApplied, x.currency),
    'Type of Recon' : x => x.isManual ? 'Manual' : 'Automatic',
    'Type of Match' : 'typeOfMatch',
    'Number of Invoice ' : x => x.matchesAggregation.count,
    'Automatic Invoice Matched' : 'autoMatchedNumber',
};

export const ReconciliationsPage = ( { booked } : {
    booked: boolean,
}) => {
    const state = useModel( State )
    
    useLocalStorage(localStorageFilterKey, {f: state.$.filter});
    
    const csvDownloader = React.useRef<CsvDownloader<Reconciliation>>(null);

    const $refresh = useLinked(false);

    const isReady = useIO( async () => {
        state.companies = globalStore.companies;
        state.selected = null;

        await Promise.all([
            state.reconciliations.fetch({ params: { booked }})
        ])

        const customerExternalIdParam = getQueryParam('customerExternalId');

        if (customerExternalIdParam) {
            // Reset local storage filters when query param filter is applied i.e.
            // inmatch/matching/reconciled?customerExternalId=CX0000061-870
            
            state.filter = new ReconciliationFilters();
          }

        if (state.reconciliations) {
            state.filter.initializeOptions(state.reconciliations, customerExternalIdParam, booked);

            if (!localStorage.getItem(localStorageFilterKey) && !customerExternalIdParam) {
                state.filter.initializeDefaults();
            }
        }
    }, [booked]);

    useIO(async () => {
        state.reconciliations.fetch({ params: { booked }});
        state.selected = null;
    }, [$refresh.value])

    const getConversationByCustExternalId = () => {
    
          let conversationByCustExternalId = []
          if (state.filter.customerExternalId && state.filter.customerExternalId.value) {
            const relationshipIds = globalStore.relationships
                .filter(rel => !!rel.Customer && state.filter.customerExternalId.value === rel.Customer.externalRelationshipId)
                .map(rel => rel.id);
    
            conversationByCustExternalId = globalStore.conversations.filter(x => relationshipIds.includes(x.relationshipId as any))
              .map(c => c.id);
            
          }
    
        return conversationByCustExternalId;
      }
    

    const filtered = useMemo(()=>
        state.reconciliations
            .filter( x => 
                state.category === 'all' ? true :
                state.category === 'auto' ? !x.isManual : 
                    x.isManual
            )
            .filter( x => x.filter( state.filter, getConversationByCustExternalId() ))
            .sort( Reconciliation.comparator( state.sortBy.attr as any, state.sortBy.asc ))
    , [ state.category, state.reconciliations['_changeToken'], state.filter["_changeToken"], state.sortBy['_changeToken'] ])

    const reverseReconciliation = () => {
        state.reconciliations.remove(state.reversing);
    };

    const counts = useMemo(() => {
          return {
            automatic: state.reconciliations.filter(x => !x.isManual).filter(x => x.filter(state.filter, getConversationByCustExternalId())).length,
            manually: state.reconciliations.filter(x => x.isManual).filter(x => x.filter(state.filter, getConversationByCustExternalId())).length
          }}
        , [state.reconciliations['_changeToken'], state.filter['_changeToken']]);

        
    const icnBtnClicked = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        csvDownloader.current.handleClick();
    };

    return (
        isReady ?
            <Page
                menuWidth={530}
                maxWidth="xl"
                left={
                    <>
                        <CompactTabs label="Reconciliations" $value={state.$.category}>
                            <Tab value="all">All ({counts.automatic + counts.manually})</Tab>
                            <Tab value="auto">Auto ({counts.automatic})</Tab>
                            <Tab value="manual">Manual ({counts.manually})</Tab>
                        </CompactTabs>
                        <FiltersList filter={state.filter} />
                        <AddFilter 
                            filter={state.filter}
                            filterAllPlaceholder="Filter by..."
                        />
                        <Flex row>
                            <SelectSortBy $attr={ state.sortBy.$.attr } $asc={ state.sortBy.$.asc } options={ booked ? sortOptionsBooked : sortOptions } />
                            <Flex grow />
                            <CsvDownloader ref={csvDownloader} filename={ `reconciliations_${asFilenameDateTime(new Date())}` } mapping={ csvFields } data={ filtered }>
                                <IconButton aria-label="Download" size="small" onClick={icnBtnClicked}>
                                    <GetAppIcon />
                                </IconButton>
                            </CsvDownloader>
                        </Flex>
                        { filtered.length ?
                            <TilesList
                                items={ filtered }
                                itemSize={ 60 }
                                renderer={ReconciliationTile(state.$.unmatching, state.$.reversing, state.$.booking, state.$.selected) }
                                selectedItem={ state.selected }
                            />
                        :
                            <Flex center stretch>
                                <Typography variant="h4">
                                    No results found.
                                </Typography>
                            </Flex>
                        }
                    </>
                }
            >
                { state.selected ? 
                    <PaymentDetails reconciliation={ state.selected } $refresh={$refresh} /> :
                    <Flex center stretch>
                        Select Reconciliation
                    </Flex>
                }
            </Page>
        :
            <Loading/>
    )
}

const ReconciliationTile = ($unmatching, $reversing, $booking, $selected) => ({ data, style, index }) => {
    const reconciliation : Reconciliation = data.items[ index ];
    const isBooked = reconciliation.status == 'Booked';
    return (
        <div style={style}>
            <SmallTile
                isSelected={ $selected.value === reconciliation }
                color={'green'}
                onClick={ () => $selected.set(reconciliation) }
                footer={
                    <>
                        <TileField label="Amount">
                            { asCurrency(reconciliation.finalCashApplied, reconciliation.currency) }
                        </TileField>
                        <TileField label="Value Date">
                            { asTimelessDate(reconciliation.valueDate) }
                        </TileField>
                        {reconciliation.unfundedInvAmount ? 
                            <TileField label="Unfunded">
                                { asCurrency(reconciliation.unfundedInvAmount, reconciliation.currency) }
                            </TileField> 
                            : undefined 
                        }
                    </>
                }
            >
                <div>{reconciliation.customer && reconciliation.customer.name}</div>
            </SmallTile>
        </div>
    )
}

const CashAppliedLabel = withStyles({
    root: {
        width: 180,
        marginRight: 8
    }
})(RowH2);

const ReversalDialog = ({ $reconciliation, onReverse, $refresh } : {
    $reconciliation : Linked<Reconciliation>,
    onReverse: () => void,
    $refresh : Linked<boolean>,
}) => {

    return (
        <PopupIODialog
            $isOpen={ toLinkedBoolean( $reconciliation ) }
            title="Are you sure you want to reverse?"
            actionLabel="Reversal"
            success="Booking is reversed"
            disabled={ false }
            doIO={ async () => {
                await execReverseReconciliation($reconciliation.value.id);
                $refresh.set(!$refresh.value);
                Events.trigger( "summary:refresh" );
            }}
            onSuccess={onReverse}
        />
    )
}

interface PaymentReconState extends AttributesMixin<typeof PaymentReconState>{}
@define class PaymentReconState extends Store {
    static attributes = {
        payment : Payment,
        companies : [ CompanyModel ],
        reconciliations : [ Reconciliation ],
        activities : [Activity],
        reversing : memberOf( 'reconciliations', Reconciliation ),
        unmatching : memberOf( 'reconciliations', Reconciliation ),
        booking : memberOf( 'reconciliations', Reconciliation ),
    };

    async fetch(){
        this.companies = globalStore.companies;
        
        await Promise.all([
            this.payment.fetch(),
            this.activities.fetch({params: {assetId: this.payment.id, assetType: 'PaymentInfo'}}),
            this.reconciliations.fetch({params: { paymentId: this.payment.id }}),
            this.payment.uploads.fetch(),
        ]);

        await Promise.all(this.reconciliations.map(x => x.fetch()));

        return this;
    }
}

const PaymentDetails = ({ reconciliation, $refresh } : { 
    reconciliation : Reconciliation,
    $refresh : Linked<boolean> 
 } ) => {

    const state = useModel(PaymentReconState);
    const isReady = useIO(
        async () => {
            state.payment.id = reconciliation.paymentId;
            await state.fetch();
        },
        [reconciliation["_changeToken"]]
      );

    const reverseReconciliation = () => {
        state.reconciliations.remove(state.reversing);
    };

    const isBooked = reconciliation.status === 'Booked';
    const $dialog = useLinked(false);
    const refreshActivities = async () => {
        await Promise.all([
            state.payment.uploads.fetch(),
            state.activities.fetch({params: {assetId: state.payment.id, assetType: 'PaymentInfo'}})
        ]);
    };

    const actions = (<>
        <Flex grow />
        { $dialog.value ?  <UploadPaymentFileDialog $isOpen={$dialog} paymentId={Number(reconciliation.paymentId)} 
            onSuccess={refreshActivities} /> : undefined }
            
        { Can(UserEntitlementCatalogs.BankEntryWrite) && <IconButton aria-label="Attach" size="medium"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                $dialog.true();
            }}>
            <AttachFileIcon />
        </IconButton> }
        { isBooked ? <>{Can(UserEntitlementCatalogs.ReverseInmatchPayment) ? 
                <Button onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        state.$.reversing.set(reconciliation)
                    }}>
                    Reversal
                </Button> : undefined }</> :
              <>
              { Can(UserEntitlementCatalogs.BankEntryConfirm) ?
              <>
                <PrimaryButton disabled={!reconciliation.ableToBook} onClick={ (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        state.$.booking.set(reconciliation);
                    }}>
                    Book
                </PrimaryButton>
                <Button onClick={(e) => { 
                        e.preventDefault();
                        e.stopPropagation();
                        state.$.unmatching.set( reconciliation )
                    }}>
                    Unmatch
                </Button> </> : undefined }
              </>
            }</>
    );
    return (
        isReady ? 
            <>
            <PaymentInformation payment={state.payment} reconciliations={state.reconciliations} activities={state.activities} 
                uploads={state.payment.uploads} actions={actions}
            />
            { state.unmatching ? <UnreconcileDialog $reconciliation={ state.$.unmatching } $refresh={$refresh} /> : undefined }
            { state.reversing ? <ReversalDialog $reconciliation={ state.$.reversing } onReverse={reverseReconciliation} $refresh={$refresh} /> : undefined }
            {state.booking ? <GenerateFeedConfirmationDialog $isOpen={toLinkedBoolean(state.$.booking)} conversationId={state.booking.conversationId}
                    onClick={async () => {
                        await execBookPayment(state.booking.id)
                        state.booking.$.bookedOn.set(new Date());
                        $refresh.set(!$refresh.value);
                        Events.trigger( "summary:refresh" );
                    }} /> : undefined }
            </> :
            <Loading/>
    )
}
